import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './Auth';
import { SnackbarProvider } from './context/SnackbarContext';
import './App.css';
import {
  Box,
  // ThemeProvider, 
  // CssBaseline 
} from '@mui/material';
// import theme from './theme';

import Header from './components/Header';

import NavMenu from './pages/NavMenu';
import Ceremonies from './pages/Ceremonies';
import WWCC from './pages/WWCC';
import Login from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import SystemSettings from './pages/SystemSettings';
import StudentData from './pages/StudentData';
import AwardsData from './pages/AwardsData';
import TenantManagement from './pages/TenantManagement';
import UserManagement from './pages/UserManagement';
import Nominate from './pages/Nominate';
import CeremonyDetail from './pages/CeremonyDetail';
import PublicWWCCForm from './pages/WWCCPublic';
import WWCCSettings from './pages/WWCCSettings';
import AuditLog from './pages/AuditLog';
import ForcePasswordChange from './pages/ForcePasswordChange';
import GovFiles from './pages/GovFiles';

// Just check if they are logged in at all
function ProtectedRoute() {
  const { user, loading } = useAuth(); // Add a loading state if you have one

  if (loading) return null; // Or a spinner
  if (!user) return <Navigate to="/login" replace />;

  // return user ? <Outlet /> : <Navigate to="/login" replace />;

  // If password change is required, only allow them to see the force-password-change page
  if (user.requiresPasswordChange && window.location.pathname !== '/force-password-change') {
    return <Navigate to="/force-password-change" replace />;
  }

  return <Outlet />;
}

// Check if the logged-in user is an admin
function AdminRoute() {
  const { user } = useAuth();
  // Allow BOTH admin and superuser to pass
  if (user.role !== 'admin' && user.role !== 'superuser') {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

// Check if the logged-in user is a SuperUser
function SuperUserRoute() {
  const { user } = useAuth();
  if (user?.role !== 'superuser') {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

function AuthenticatedLayout() {
  return (
    <Box className="app">
      <Header />

      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <NavMenu />
        <Box component="main" className="main">
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

function App() {
  const { user } = useAuth();

  return (
    <SnackbarProvider>
      <Router>
        <Routes>

          {/* Public route – no sidebar, no header */}
          <Route path="/login" element={<Login />} />
          <Route path="/submit-wwcc/:compositeKey" element={<PublicWWCCForm />} />

          <Route element={<ProtectedRoute />}>

            {/* This route is OUTSIDE the AuthenticatedLayout so they don't see the sidebar */}
            <Route path="/force-password-change" element={<ForcePasswordChange />} />

            {!user?.requiresPasswordChange && (
              <Route element={<AuthenticatedLayout />}>
                <Route path="/" element={<Dashboard key={user?.tenant_id || 'global'} />} />
                <Route path="/ceremonies" element={<Ceremonies />} />
                <Route path="/ceremonies/:ceremonyId" element={<CeremonyDetail />} />
                <Route path="/wwcc" element={<WWCC />} />
                <Route path="/nominations/:ceremonyId" element={<Nominate />} />

                {/* Wrap Admin-only pages in the AdminRoute */}
                <Route element={<AdminRoute />}>
                  <Route path="/settings" />
                  <Route path="/settings/staff" element={<UserManagement mode="staff" />} />
                  <Route path="/settings/students" element={<StudentData />} />
                  <Route path="/settings/awards" element={<AwardsData />} />
                  <Route path="/settings/wwcc-import" element={<WWCCSettings />} />
                  <Route path="/settings/settings" element={<SystemSettings />} />
                </Route>

                <Route element={<SuperUserRoute />}>
                  <Route path="/admin/tenants" element={<TenantManagement />} />
                  <Route path="/admin/super-users" element={<UserManagement mode="superuser" />} />
                  <Route path="/admin/audit-log" element={<AuditLog />} />
                  <Route path="/admin/gov-files" element={<GovFiles />} />
                </Route>

              </Route>
            )}

          </Route>

          {/* Optional: catch-all redirect to login if someone hits unknown route */}
          <Route path="*" element={<Navigate to="/login" replace />} />

        </Routes>
      </Router>
    </SnackbarProvider>
  );
}

export default App;